/**
 * Music theory, as data — the shared core of BB Academy's interactive
 * lessons and the ear trainer (Gordon 2026-10-31: "more visuals for music
 * theory … learn relative pitch and perfect pitch"). Pure: no audio, no UI.
 * Equal temperament, A4 = 440 Hz, MIDI note numbers (60 = middle C).
 */

export const NOTE_SHARP = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
export const NOTE_FLAT = ['C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B'];
/** One name per pitch class, the way most people read it. */
export const NOTE_COMMON = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B'];
const BLACK = new Set([1, 3, 6, 8, 10]);

export const pc = (midi: number) => ((midi % 12) + 12) % 12;
export const isBlack = (midi: number) => BLACK.has(pc(midi));
export const octaveOf = (midi: number) => Math.floor(midi / 12) - 1;
export const midiToFreq = (midi: number, a4 = 440) => a4 * 2 ** ((midi - 69) / 12);
export function noteName(midi: number, opts: { flats?: boolean; octave?: boolean } = {}) {
  const n = (opts.flats ? NOTE_FLAT : NOTE_SHARP)[pc(midi)];
  return opts.octave ? `${n}${octaveOf(midi)}` : n;
}

/* ── Intervals ─────────────────────────────────────────────────────────── */

export type IntervalId = 'm2' | 'M2' | 'm3' | 'M3' | 'P4' | 'TT' | 'P5' | 'm6' | 'M6' | 'm7' | 'M7' | 'P8';
export interface IntervalDef {
  id: IntervalId;
  semitones: number;
  /** Translation key under earTraining.intervals.* */
  key: string;
  /** Songs that start with this leap — titles only, as memory hooks. */
  songsUp: string[];
  songsDown: string[];
}
export const INTERVALS: IntervalDef[] = [
  { id: 'm2', semitones: 1, key: 'm2', songsUp: ['Jaws (theme)', 'Für Elise'], songsDown: ['Joy to the World', 'Für Elise (first notes)'] },
  { id: 'M2', semitones: 2, key: 'M2', songsUp: ['Happy Birthday', 'Silent Night'], songsDown: ['Mary Had a Little Lamb', 'Three Blind Mice'] },
  { id: 'm3', semitones: 3, key: 'm3', songsUp: ['Greensleeves', 'Smoke on the Water (riff)'], songsDown: ['Hey Jude', 'Frosty the Snowman'] },
  { id: 'M3', semitones: 4, key: 'M3', songsUp: ['When the Saints Go Marching In', 'Kumbaya'], songsDown: ['Swing Low, Sweet Chariot', 'Beethoven’s 5th (opening)'] },
  { id: 'P4', semitones: 5, key: 'P4', songsUp: ['Here Comes the Bride', 'Amazing Grace'], songsDown: ['O Come, All Ye Faithful', 'Born Free'] },
  { id: 'TT', semitones: 6, key: 'TT', songsUp: ['The Simpsons (theme)', 'Maria (West Side Story)'], songsDown: ['Black Sabbath (riff)', 'YYZ (Rush)'] },
  { id: 'P5', semitones: 7, key: 'P5', songsUp: ['Twinkle Twinkle Little Star', 'Star Wars (main theme)'], songsDown: ['The Flintstones (theme)', 'Feelings'] },
  { id: 'm6', semitones: 8, key: 'm6', songsUp: ['The Entertainer (3rd–4th note)', 'In My Life'], songsDown: ['Love Story (theme)', 'Call Me Irresponsible'] },
  { id: 'M6', semitones: 9, key: 'M6', songsUp: ['My Bonnie Lies over the Ocean', 'NBC chimes'], songsDown: ['Nobody Knows the Trouble I’ve Seen', 'Man in the Mirror (chorus)'] },
  { id: 'm7', semitones: 10, key: 'm7', songsUp: ['Star Trek (original theme)', 'Somewhere (West Side Story)'], songsDown: ['An American in Paris', 'Watermelon Man'] },
  { id: 'M7', semitones: 11, key: 'M7', songsUp: ['Take On Me (chorus leap)', 'Pure Imagination'], songsDown: ['I Love You (Cole Porter)', 'Have You Met Miss Jones?'] },
  { id: 'P8', semitones: 12, key: 'P8', songsUp: ['Somewhere Over the Rainbow', 'Singin’ in the Rain'], songsDown: ['Willow Weep for Me', 'There’s No Business Like Show Business'] },
];
export const intervalById = (id: string) => INTERVALS.find(i => i.id === id)!;

/* ── Chords ────────────────────────────────────────────────────────────── */

export type ChordQuality = 'maj' | 'min' | 'dim' | 'aug' | 'sus2' | 'sus4' | 'maj7' | 'dom7' | 'min7' | 'm7b5' | 'dim7'
  // The rest of the chord library (Gordon 2026-11-02: "all chords and scales").
  | 'power' | 'maj6' | 'min6' | 'add9' | 'dom9' | 'maj9' | 'min9' | 'dom11' | 'dom13';
export const CHORDS: Record<ChordQuality, { tones: number[]; symbol: string }> = {
  maj: { tones: [0, 4, 7], symbol: '' },
  min: { tones: [0, 3, 7], symbol: 'm' },
  dim: { tones: [0, 3, 6], symbol: '°' },
  aug: { tones: [0, 4, 8], symbol: '+' },
  sus2: { tones: [0, 2, 7], symbol: 'sus2' },
  sus4: { tones: [0, 5, 7], symbol: 'sus4' },
  maj7: { tones: [0, 4, 7, 11], symbol: 'maj7' },
  dom7: { tones: [0, 4, 7, 10], symbol: '7' },
  min7: { tones: [0, 3, 7, 10], symbol: 'm7' },
  m7b5: { tones: [0, 3, 6, 10], symbol: 'ø7' },
  dim7: { tones: [0, 3, 6, 9], symbol: '°7' },
  power: { tones: [0, 7], symbol: '5' },
  maj6: { tones: [0, 4, 7, 9], symbol: '6' },
  min6: { tones: [0, 3, 7, 9], symbol: 'm6' },
  add9: { tones: [0, 4, 7, 14], symbol: 'add9' },
  dom9: { tones: [0, 4, 7, 10, 14], symbol: '9' },
  maj9: { tones: [0, 4, 7, 11, 14], symbol: 'maj9' },
  min9: { tones: [0, 3, 7, 10, 14], symbol: 'm9' },
  // 11 and 13 as players voice them: the 11 without its third, the 13 without its fifth and eleventh.
  dom11: { tones: [0, 7, 10, 14, 17], symbol: '11' },
  dom13: { tones: [0, 4, 10, 14, 21], symbol: '13' },
};
/** Chord notes from a root (MIDI); `inversion` moves the lowest notes up an octave. */
export function chordNotes(root: number, quality: ChordQuality, inversion = 0): number[] {
  const notes = CHORDS[quality].tones.map(t => root + t);
  for (let i = 0; i < inversion; i++) notes.push((notes.shift() as number) + 12);
  return notes;
}
export const chordSymbol = (rootPc: number, q: ChordQuality) => `${NOTE_COMMON[rootPc]}${CHORDS[q].symbol}`;

/** "Dm7" → { rootPc: 2, quality: 'min7' }. Accepts ♯/♭ or #/b, °, ø, +, maj7, m7b5, sus2/4, dim, aug. */
export function parseChordSymbol(sym: string): { rootPc: number; quality: ChordQuality } {
  const m = /^([A-G])([#♯b♭]?)(.*)$/.exec(sym.trim());
  if (!m) throw new Error(`Bad chord "${sym}"`);
  const base = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }[m[1] as 'C'];
  const acc = m[2] === '#' || m[2] === '♯' ? 1 : m[2] === 'b' || m[2] === '♭' ? -1 : 0;
  const rest = m[3].replace(/\s+/g, '');
  const table: Record<string, ChordQuality> = {
    '': 'maj', maj: 'maj', M: 'maj', m: 'min', min: 'min', '-': 'min', '°': 'dim', dim: 'dim', '+': 'aug', aug: 'aug',
    sus2: 'sus2', sus4: 'sus4', sus: 'sus4', maj7: 'maj7', M7: 'maj7', 'Δ7': 'maj7', 'Δ': 'maj7', '7': 'dom7',
    m7: 'min7', min7: 'min7', '-7': 'min7', 'ø': 'm7b5', 'ø7': 'm7b5', m7b5: 'm7b5', 'm7♭5': 'm7b5', '°7': 'dim7', dim7: 'dim7',
    '5': 'power', '6': 'maj6', maj6: 'maj6', m6: 'min6', min6: 'min6', add9: 'add9', '9': 'dom9', maj9: 'maj9', M9: 'maj9',
    m9: 'min9', min9: 'min9', '11': 'dom11', '13': 'dom13',
  };
  const quality = table[rest];
  if (!quality) throw new Error(`Unknown chord quality "${rest}" in "${sym}"`);
  return { rootPc: (base + acc + 12) % 12, quality };
}

/* ── Scales & keys ─────────────────────────────────────────────────────── */

export type ScaleId = 'major' | 'minor' | 'harmonicMinor' | 'melodicMinor' | 'majorPentatonic' | 'minorPentatonic' | 'blues' | 'dorian' | 'mixolydian' | 'lydian' | 'phrygian'
  | 'locrian' | 'wholeTone' | 'chromatic';
export const SCALES: Record<ScaleId, number[]> = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  harmonicMinor: [0, 2, 3, 5, 7, 8, 11],
  melodicMinor: [0, 2, 3, 5, 7, 9, 11],
  majorPentatonic: [0, 2, 4, 7, 9],
  minorPentatonic: [0, 3, 5, 7, 10],
  blues: [0, 3, 5, 6, 7, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  locrian: [0, 1, 3, 5, 6, 8, 10],
  wholeTone: [0, 2, 4, 6, 8, 10],
  chromatic: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
};
export function scaleNotes(root: number, scale: ScaleId): number[] {
  return [...SCALES[scale].map(s => root + s), root + 12];
}
/** "W W H W W W H" — the steps between scale notes (W = 2, H = 1, and 3 = "W+H"). */
export function scaleSteps(scale: ScaleId): number[] {
  const s = [...SCALES[scale], 12];
  return s.slice(1).map((v, i) => v - s[i]);
}
/** Keys that read with flats (F, B♭, E♭, A♭, D♭, G♭ major and their minors). */
export function prefersFlats(keyPc: number, minor = false) {
  const majorPc = minor ? (keyPc + 3) % 12 : keyPc;
  return [5, 10, 3, 8, 1, 6].includes(majorPc);
}

/** Circle of fifths, clockwise from C: major keys and their relative minors. */
export const CIRCLE_OF_FIFTHS = Array.from({ length: 12 }, (_, i) => {
  const major = (i * 7) % 12;
  const sharps = i <= 6 ? i : i - 12; // + = sharps, − = flats (F♯/G♭ at 6)
  return { major, minor: (major + 9) % 12, accidentals: sharps };
});

/* ── Harmony in a key ──────────────────────────────────────────────────── */

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];
const MAJOR_TRIADS: ChordQuality[] = ['maj', 'min', 'min', 'maj', 'maj', 'min', 'dim'];
const MINOR_TRIADS: ChordQuality[] = ['min', 'dim', 'maj', 'min', 'min', 'maj', 'maj'];
export function romanFor(degree: number, q: ChordQuality) {
  const r = ROMAN[degree];
  const lower = q === 'min' || q === 'dim' || q === 'min7' || q === 'm7b5' || q === 'dim7';
  return (lower ? r.toLowerCase() : r) + (q === 'dim' ? '°' : q === 'aug' ? '+' : '');
}
/** The seven triads of a key: I ii iii IV V vi vii° (major) or i ii° III iv v VI VII (minor). */
export function diatonicTriads(keyPc: number, minor = false) {
  const scale = SCALES[minor ? 'minor' : 'major'];
  const qualities = minor ? MINOR_TRIADS : MAJOR_TRIADS;
  return scale.map((step, d) => ({ degree: d, rootPc: (keyPc + step) % 12, quality: qualities[d], roman: romanFor(d, qualities[d]) }));
}

/** Common progressions, as scale degrees (0-based) in a major key. */
export interface Progression { id: string; degrees: number[] }
export const PROGRESSIONS: Progression[] = [
  { id: 'I-IV-V-I', degrees: [0, 3, 4, 0] },
  { id: 'I-V-vi-IV', degrees: [0, 4, 5, 3] },
  { id: 'ii-V-I', degrees: [1, 4, 0] },
  { id: 'I-vi-IV-V', degrees: [0, 5, 3, 4] },
  { id: 'vi-IV-I-V', degrees: [5, 3, 0, 4] },
  { id: 'I-IV-I-V', degrees: [0, 3, 0, 4] },
  { id: 'I-iii-IV-V', degrees: [0, 2, 3, 4] },
  { id: 'IV-V-iii-vi', degrees: [3, 4, 2, 5] },
];
/** A progression's chords as MIDI voicings around `keyRoot`, with smooth-ish voice leading. */
export function progressionVoicings(keyRoot: number, degrees: number[]): number[][] {
  const triads = diatonicTriads(pc(keyRoot));
  return degrees.map(d => {
    const t = triads[d];
    let root = keyRoot - pc(keyRoot) + t.rootPc;
    if (root > keyRoot + 5) root -= 12; // keep chords in one register
    return chordNotes(root, t.quality);
  });
}

/* ── Scale degrees (functional ear training) ───────────────────────────── */

export const DEGREE_LABELS = ['1', '♭2', '2', '♭3', '3', '4', '♯4', '5', '♭6', '6', '♭7', '7'];
export const SOLFEGE = ['Do', 'Ra', 'Re', 'Me', 'Mi', 'Fa', 'Fi', 'Sol', 'Le', 'La', 'Te', 'Ti'];
/** Semitones above the key root for the seven major-scale degrees. */
export const DIATONIC_DEGREES = [0, 2, 4, 5, 7, 9, 11];
